// Cloud saves (§4.2) + verified-run submission (§5.1). Local-first: the game always saved locally
// already; this mirrors it to the server at each new day and on tab hide, when signed in.
import { signal } from "@preact/signals";
import type { Engine } from "@degen/sim/engine";
import { dayOf } from "@degen/sim/state";
import { serialize, deserialize } from "@degen/sim/save";
import { netWorth } from "@degen/sim/player";
import { summarize } from "@degen/sim/run";
import { flag } from "@degen/shared/flags";
import type { CloudSaveInfo, SaveMeta, SaveUploadResult } from "@degen/shared/api";
import { api, ApiError } from "./api";
import { deviceId } from "./identity";
import { session } from "./session";
import { whenSignedIn } from "./auth";
import { saveGame } from "../saveStore";
import { track } from "./telemetry";

const BASE_KEY = "degen-world:cloud-base";
export const conflict = signal<{ local: SaveMeta; cloud: CloudSaveInfo } | null>(null);
export const syncState = signal<"idle" | "syncing" | "synced" | "error">("idle");
export const lastSynced = signal<string | null>(null);
/** While swapping to the cloud life, the running game must not save over it (tab-hide fires on navigation). */
export const lifeSwitch = { on: false };

let engineRef: Engine | null = null;
let lastRunDay = 0;

const baseId = (): number | null => {
  try {
    const v = localStorage.getItem(BASE_KEY);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
};
const setBase = (id: number) => {
  try {
    localStorage.setItem(BASE_KEY, String(id));
  } catch {
    /* ignore */
  }
};

function metaOf(e: Engine): SaveMeta {
  const s = e.state;
  return { saveVersion: s.version, tick: s.tick, day: dayOf(s.tick), deviceId, netWorth: Math.round(netWorth(s)), cash: Math.round(s.player.cash), followers: s.player.followers, rank: s.player.rank };
}

export async function syncNow(force = false): Promise<void> {
  const e = engineRef;
  if (!e || !session.value || !flag("cloudSaves") || e.state.shared) return;
  syncState.value = "syncing";
  try {
    const r = await api<SaveUploadResult>("PUT", "/saves", { ...metaOf(e), data: serialize(e.state), baseId: baseId(), force });
    if (r.ok) {
      setBase(r.id);
      syncState.value = "synced";
      lastSynced.value = new Date().toLocaleTimeString();
    }
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      conflict.value = (err.body as { conflict: { local: SaveMeta; cloud: CloudSaveInfo } }).conflict;
      syncState.value = "idle";
      return;
    }
    syncState.value = "error";
  }
}

/** The player picked a side of a conflict: keep this device's life, or continue the cloud one. */
export async function resolveConflict(choice: "local" | "cloud") {
  const c = conflict.value;
  conflict.value = null;
  if (!c) return;
  track("save_conflict", { choice });
  if (choice === "local") return syncNow(true);
  const cloud = await api<CloudSaveInfo & { data: string }>("GET", "/saves/latest");
  const state = deserialize(cloud.data);
  lifeSwitch.on = true;
  await saveGame(state);
  setBase(cloud.id);
  location.href = location.pathname; // boot from the new local save (drop ?new / ?skip params)
}

/** Submit the run for verification once per in-game day (Phase 2). */
export async function submitRun() {
  const e = engineRef;
  if (!e || !session.value || !flag("verifiedRuns") || e.state.shared || !e.state.run || e.state.run.legacy) return;
  const day = dayOf(e.state.tick);
  if (day === lastRunDay) return;
  lastRunDay = day;
  try {
    await api("POST", "/runs", { seed: e.state.seed, run: e.state.run, claimed: summarize(e.state) });
  } catch {
    lastRunDay = 0; // retry next time
  }
}

export function startCloud(e: Engine) {
  engineRef = e;
  whenSignedIn(() => {
    void syncNow();
    void submitRun();
  });
  if (session.value) void syncNow();
}

/** Soft prompt (§4.1): after Day 3 and after the first rank-up, if still a guest. */
export const signInPrompt = signal<null | "day3" | "rankup">(null);
export function maybePromptSignIn(reason: "day3" | "rankup") {
  if (session.value) return;
  try {
    const k = `degen-world:prompted:${reason}`;
    if (localStorage.getItem(k)) return;
    localStorage.setItem(k, "1");
  } catch {
    /* ignore */
  }
  signInPrompt.value = reason;
}
